import { useEffect, useState } from 'react';
import { getComplianceRegister, listMailboxes, recordPoshAssessment, type ComplianceRegister as Register, type MailboxRow } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { Icon } from '../../components/Icon';
import { Pending } from './shared';

const CATEGORIES = ['employees', 'interns', 'trainees', 'contract_workers', 'consultants', 'volunteers'] as const;

export function ComplianceRegister() {
  const { can, session } = useAuth();
  const [register, setRegister] = useState<Register | null>(null);
  const [mailboxes, setMailboxes] = useState<MailboxRow[]>([]);
  const [counts, setCounts] = useState<Record<string, string>>(Object.fromEntries(CATEGORIES.map((c) => [c, ''])));
  const [basis, setBasis] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([
    getComplianceRegister(),
    listMailboxes().catch(() => ({ items: [] as MailboxRow[] })),
  ])
    .then(([r, m]) => { setRegister(r); setMailboxes(m.items); setError(null); })
    .catch((e: Error) => setError(e.message));

  useEffect(() => { load(); }, []);

  const total = CATEGORIES.reduce((n, c) => n + (Number(counts[c]) || 0), 0);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await recordPoshAssessment({
        workforceCounts: Object.fromEntries(CATEGORIES.map((c) => [c, Number(counts[c]) || 0])),
        countingBasisNotes: basis || null,
      });
      setNotice(`Assessment recorded. ${res.totalCounted} people counted; the determination remains pending professional confirmation.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the assessment.');
    } finally { setBusy(false); }
  }

  const orgName = session?.tenant.name ?? '';

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow"><Icon name="shieldCheck" size="xs" /><span>Compliance</span></span>
          <h1>Compliance Register</h1>
        </div>
      </div>

      {error && <div className="banner error" style={{ marginBottom: 14 }}>{error}</div>}
      {notice && <div className="banner info" style={{ marginBottom: 14 }}>{notice}</div>}

      {/three thirty/i.test(orgName) && (
        <div className="banner warn" style={{ marginBottom: 14 }}>
          <strong>Unresolved: organisation legal identity.</strong> The organisation on this tenant is recorded as
          “{orgName}”, which is not ABNY Business Solutions Pvt. Ltd. Nothing has been renamed. Compliance items below
          cannot be filed against the correct entity until management supplies ABNY's legal name, PAN, GSTIN and
          registered address.
        </div>
      )}

      <div className="org-grid">
        <div className="panel">
          <div className="panel-head">
            <strong>Register</strong>
            <span className="muted" style={{ fontSize: 12 }}>Applicability is recorded, never computed</span>
          </div>
          {!register || register.items.length === 0 ? (
            <div className="empty"><strong>No compliance items recorded</strong></div>
          ) : (
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th style={{ width: 120 }}>Category</th>
                    <th style={{ width: 150 }}>Applicability</th>
                    <th style={{ width: 130 }}>Responsible</th>
                    <th style={{ width: 110 }}>Status</th>
                    <th style={{ width: 150 }}>Professional review</th>
                  </tr>
                </thead>
                <tbody>
                  {register.items.map((i) => (
                    <tr key={i.id}>
                      <td><strong>{i.item}</strong></td>
                      <td className="muted">{i.category ?? '—'}</td>
                      <td><span className={`org-chip org-chip-${i.applicability}`}>{i.applicability.replace(/_/g, ' ')}</span></td>
                      <td><Pending>{i.responsibleEmployeeId}</Pending></td>
                      <td><span className={`org-chip org-chip-${i.status}`}>{i.status}</span></td>
                      <td>{i.professionalReviewRequired ? <Pending>{i.professionalReviewedBy}</Pending> : <span className="muted">Not required</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="muted org-footnote">{register?.note}</p>
        </div>

        <div className="org-side">
          <div className="panel">
            <div className="panel-head"><strong>POSH applicability</strong></div>
            {register?.poshAssessments.length ? (
              <ul className="org-plain-list">
                {register.poshAssessments.map((a) => (
                  <li key={a.id}>
                    <span>
                      <strong className="mono">{a.totalCounted}</strong> counted
                      <div className="muted" style={{ fontSize: 12 }}>{a.assessmentDate.slice(0, 10)}</div>
                    </span>
                    <span className="org-chip org-chip-pending_assessment">{a.determination.replace(/_/g, ' ')}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="empty"><strong>No assessment recorded</strong></div>
            )}

            {can('compliance.manage') && (
              <form onSubmit={submit} className="org-form org-form-stack" style={{ paddingTop: 12 }}>
                {CATEGORIES.map((c) => (
                  <label key={c}>
                    <span>{c.replace(/_/g, ' ')}</span>
                    <input type="number" min={0} value={counts[c]} onChange={(e) => setCounts({ ...counts, [c]: e.target.value })} />
                  </label>
                ))}
                <label><span>Counting basis</span><input value={basis} onChange={(e) => setBasis(e.target.value)} placeholder="How the count was arrived at" /></label>
                <div className="org-form-actions">
                  <button className="btn-primary" type="submit" disabled={busy}>Record count of {total}</button>
                </div>
              </form>
            )}
            <p className="muted org-footnote">
              The count is evidence for a professional, not a conclusion. The system records workforce categories and
              never determines applicability from a payroll headcount.
            </p>
          </div>

          <div className="panel">
            <div className="panel-head"><strong>Organizational mailboxes</strong></div>
            {mailboxes.length === 0 ? (
              <div className="empty"><strong>None recorded</strong></div>
            ) : (
              <ul className="org-plain-list">
                {mailboxes.map((m) => (
                  <li key={m.id}>
                    <span className="mono" style={{ fontSize: 12 }}>{m.address}</span>
                    <span className="org-chip org-chip-pending_confirmation"><Pending>{m.classification}</Pending></span>
                  </li>
                ))}
              </ul>
            )}
            <p className="muted org-footnote">An address is not evidence that a person owns the mailbox.</p>
          </div>
        </div>
      </div>
    </>
  );
}
